import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../cn';

export type ToastTone = 'info' | 'ok' | 'warn' | 'bad';
export interface ToastItem {
  id: number;
  text: string;
  tone: ToastTone;
  leaving?: boolean;
}

const SHOW_MS = 3500;
const LEAVE_MS = 260;

const Ctx = createContext<{ push: (text: string, tone?: ToastTone) => void } | null>(null);

export function useToast() {
  const c = useContext(Ctx);
  return c ?? { push: (t: string) => console.info('[toast]', t) };
}

/** Bottom-centred stack of frosted notices: each rises in from below, holds 3.5 s, then sinks back out. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const push = useCallback((text: string, tone: ToastTone = 'info') => {
    const id = ++seq.current;
    setItems((s) => [...s, { id, text, tone }]);
    setTimeout(() => setItems((s) => s.map((i) => (i.id === id ? { ...i, leaving: true } : i))), SHOW_MS);
    setTimeout(() => setItems((s) => s.filter((i) => i.id !== id)), SHOW_MS + LEAVE_MS);
  }, []);
  const v = useMemo(() => ({ push }), [push]);
  return (
    <Ctx.Provider value={v}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4 md:!bottom-6" style={{ bottom: 'calc(66px + env(safe-area-inset-bottom))' }}>
        {items.map((t) => (
          <Toast key={t.id} {...t} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function Toast({ text, tone, leaving }: Pick<ToastItem, 'text' | 'tone' | 'leaving'>) {
  // Mount hidden below, then slide up on the next frame so the transition runs; `leaving` runs it back down.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => setShown(true)) : (setShown(true), 0);
    return () => {
      if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    };
  }, []);
  const visible = shown && !leaving;
  return (
    <div
      role="status"
      style={{ background: 'color-mix(in srgb, var(--panel) 72%, transparent)' }}
      className={cn(
        'pointer-events-auto flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-base font-medium text-fg shadow-[0_12px_40px_rgba(0,0,0,.35)] backdrop-blur-xl backdrop-saturate-150',
        'transition-all duration-[260ms] ease-out motion-reduce:transition-none',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      )}
    >
      <span aria-hidden className={cn('inline-block h-2 w-2 flex-none rounded-full', tone === 'ok' && 'bg-ok', tone === 'warn' && 'bg-warn', tone === 'bad' && 'bg-bad', tone === 'info' && 'bg-fg4')} />
      {text}
    </div>
  );
}
